import "leaflet/dist/leaflet.css";
import { useEffect, useState } from "react";
import {
  Accordion,
  Badge,
  Button,
  Card,
  Col,
  Container,
  Form,
  Modal,
  Row,
} from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaBox,
  FaCalendarAlt,
  FaCar,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaClock,
  FaCouch,
  FaMapMarkerAlt,
  FaPhone,
  FaRocket,
  FaRoute,
  FaStar,
  FaTimes,
  FaUser,
  FaWeightHanging,
} from "react-icons/fa";
import { useNavigate, useSearchParams } from "react-router-dom";
import Swal from "sweetalert2";
import { driverAPI, postalAreaAPI } from "../../../api/apiSystem";
import {
  ArrowIcon,
  CalendarIcon,
  DeliverPointIcon,
  AlarmIcon,
} from "../../../assets/icons";
import { formatDisplayDate, formatNumber } from "../../../utils/helpers";
import ClientChatBox from "../../chat/ClientChatBox";
import NavbarMenu from "../../heroSection/NavbarMenu";
import PaginationComponent from "../../PaginationComponent";
import DriverChatModal from "../DriverChatModal";
import "../PageOne/DarkTheme.css";
import BookingDetailsPage from "../PageTwo/BookingDetailsPage";
import TransportDetailsPage from "../PageTwo/TransportDetailsPage";

// Transport Images
import animalTransportImg from "../../../assets/images/driver_transport_icon/animal_transport.png";
import bicycleImg from "../../../assets/images/driver_transport_icon/bicycle.png";
import buildingMaterialsImg from "../../../assets/images/driver_transport_icon/building_materials.png";
import carImg from "../../../assets/images/driver_transport_icon/car.png";
import documentImg from "../../../assets/images/driver_transport_icon/document.png";
import electricalImg from "../../../assets/images/driver_transport_icon/electrical.png";
import furnitureImg from "../../../assets/images/driver_transport_icon/furniture.png";
import motorcycleImg from "../../../assets/images/driver_transport_icon/motorcycle.png";
import movingImg from "../../../assets/images/driver_transport_icon/moving.png";
import otherImg from "../../../assets/images/driver_transport_icon/other.png";
import {
  default as paketeImg,
  default as regularImg,
} from "../../../assets/images/driver_transport_icon/pakete.png";
import paletteImg from "../../../assets/images/driver_transport_icon/palette.png";
import refrigeratorImg from "../../../assets/images/driver_transport_icon/refrigerator.png";
import fastImg from "../../../assets/images/driver_transport_icon/rocket.png";
import tiresImg from "../../../assets/images/driver_transport_icon/tires.png";

const DriverRequest = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const getInitialType = () => {
    const urlType = searchParams.get("type");
    return ["fast", "regular", "furnitures", "cars"].includes(urlType)
      ? urlType
      : "fast";
  };
  const [currentType, setCurrentType] = useState(getInitialType());
  const [vehicles, setVehicles] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [offCanvasOpen, setOffCanvasOpen] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [selectedTransport, setSelectedTransport] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userType, setUserType] = useState(
    typeof window !== "undefined" ? localStorage.getItem("userType") : null,
  );

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const [receivedItems, setReceivedItems] = useState({});
  const [showReceiveModal, setShowReceiveModal] = useState(false);
  const [showDeliverModal, setShowDeliverModal] = useState(false);
  const [receiveDate, setReceiveDate] = useState("");
  const [deliverDate, setDeliverDate] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [error, setError] = useState(null);
  const [orderStatus, setOrderStatus] = useState({});
  const [activeKey, setActiveKey] = useState(null);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [selectedStageNumber, setSelectedStageNumber] = useState(null);

  // SweetAlert configuration
  const showSweetAlert = (icon, title, text, confirmButtonText = "OK") => {
    return Swal.fire({
      icon,
      title,
      text,
      confirmButtonText,
      customClass: {
        confirmButton: "btn btn-primary",
      },
    });
  };

  const [receivedLoading, setReceivedLoading] = useState(false);
  const [deliverLoading, setDeliverLoading] = useState(false);

  const openReceiveConfirm = (order, stageNumber = null) => {
    setSelectedItem(order);
    setSelectedStageNumber(stageNumber);
    setShowReceiveModal(true);
    setReceiveDate("");
    setError(null);
  };

  const openDeliverConfirm = (order, stageNumber = null) => {
    setSelectedItem(order);
    setSelectedStageNumber(stageNumber);
    setShowDeliverModal(true);
    setDeliverDate("");
    setError(null);
  };

  const showErrorAlert = (text) => {
    return showSweetAlert("error", t("driverRequest.alerts.error"), text);
  };

  const showSuccessAlert = (text) => {
    return showSweetAlert("success", t("driverRequest.alerts.success"), text);
  };

  const showWarningAlert = (text) => {
    return showSweetAlert("warning", t("driverRequest.alerts.warning"), text);
  };

  const fetchChatStatistics = async (item) => {
    try {
      let transportId;
      let orderId = item.id;

      if (currentType === "furnitures" || currentType === "cars") {
        transportId = item.transport?.id;
        if (!transportId) {
          transportId = item.transport?.transport_id;
        }
      } else {
        transportId = item.booking?.id;
        if (!transportId) {
          transportId = item.booking?.booking_id;
        }
      }

      console.log("📊 Fetching chat statistics:", { transportId, orderId });

      if (!transportId || !orderId) return 0;

      const result = await driverAPI.transportOrderChat.getMessages(
        transportId,
        orderId,
      );

      console.log("📊 Chat statistics response:", result);

      if (result.success) {
        const unreadCount = result.data?.data.statistics?.unread_count;
        console.log("📊 Unread count for order", unreadCount);
        return unreadCount;
      }
    } catch (error) {
      console.error("❌ Error fetching chat statistics:", error);
    }
    return 0;
  };

  const handleMessagesRead = (orderId) => {
    setUnreadCounts((prev) => ({
      ...prev,
      [orderId]: 0,
    }));
  };

  const handleOpenChat = async (item) => {
    if (!item) {
      showErrorAlert(t("driverOffer.alerts.noItemAvailable"));
      return;
    }

    if (!item.id) {
      showErrorAlert(t("driverOffer.alerts.orderIdMissing"));
      return;
    }

    let transportId;
    let orderId = item.id;

    if (currentType === "furnitures" || currentType === "cars") {
      transportId = item.transport?.id;
      if (!transportId) {
        transportId = item.transport?.transport_id;
      }
    } else {
      transportId = item.booking?.id;
      if (!transportId) {
        transportId = item.booking?.booking_id;
      }
    }

    if (!transportId) {
      showErrorAlert(t("driverOffer.alerts.transportInfoMissing"));
      return;
    }
    setSelectedTransport({
      transportId: transportId,
      orderId: orderId,
    });

    setShowChatModal(true);

    handleMessagesRead(orderId);
  };

  useEffect(() => {
    if (!loading && data.length > 0) {
      const fetchAllUnreadCounts = async () => {
        const counts = {};

        for (const item of data) {
          let transportId;
          const orderId = item.id;

          if (currentType === "furnitures" || currentType === "cars") {
            transportId = item.transport?.id || item.transport?.transport_id;
          } else {
            transportId = item.booking?.id || item.booking?.booking_id;
          }

          if (transportId && orderId) {
            const unreadCount = await fetchChatStatistics(item);
            counts[orderId] = unreadCount;
          }
        }

        setUnreadCounts(counts);
      };

      fetchAllUnreadCounts();
    }
  }, [data, loading, currentType]);

  useEffect(() => {
    if (!loading && data.length > 0) {
      const openChat = searchParams.get("openChat") === "true";
      const orderIdParam = searchParams.get("orderId");
      const urlType = searchParams.get("type");

      if (openChat && orderIdParam && urlType === currentType) {
        const matchingItem = data.find(
          (item) => String(item.id) === String(orderIdParam)
        );
        if (matchingItem) {
          setTimeout(() => {
            handleOpenChat(matchingItem);
            // Clean up the URL search params so it doesn't open again on reload
            navigate(window.location.pathname, { replace: true });
          }, 500);
        }
      }
    }
  }, [data, loading, currentType, searchParams, navigate]);

  useEffect(() => {
    const fetchVehicles = async () => {
      try {
        const res = await postalAreaAPI.index();
        if (res.success && Array.isArray(res.data?.data)) {
          setVehicles(res.data.data);
        } else {
          console.warn("⚠️ Unexpected postal area response:", res);
        }
      } catch (err) {
        console.error("❌ Error fetching vehicles:", err);
        showErrorAlert(t("driverRequest.alerts.fetchVehiclesError"));
      }
    };
    fetchVehicles();
  }, []);

  const handleFetch = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        page,
        per_page: pagination.per_page,
      };

      let response;

      if (currentType === "regular") {
        response = await driverAPI.bookingsOrders.regularAccepted({}, params);
      } else if (currentType === "fast") {
        response = await driverAPI.fastOrders.accepted({}, params);
      } else if (currentType === "cars") {
        response = await driverAPI.transportsOrders.carAccepted({}, params);
      } else if (currentType === "furnitures") {
        response = await driverAPI.transportsOrders.furnitureAccepted(
          {},
          params,
        );
      } else {
        throw new Error(`Invalid currentType: ${currentType}`);
      }

      if (response && response.success) {
        const fetchedData = Array.isArray(response.data?.data)
          ? response.data.data
          : Array.isArray(response.data?.data)
            ? response.data.data
            : [];

        const validatedData = fetchedData.map((item) => {
          if (currentType === "regular") {
            return {
              ...item,
              booking: item.booking || {}, // تأكد من وجود booking
            };
          } else {
            return {
              ...item,
              transport: item.transport || {}, // تأكد من وجود transport
            };
          }
        });
        setData(validatedData);

        console.log("Received date:", validatedData);

        const newReceivedItems = {};
        const newOrderStatus = {};

        fetchedData.forEach((item) => {
          if (item.status === "received" || item.status === "accepted") {
            newReceivedItems[item.id] = true;
          } else if (
            item.status === "completed" ||
            item.status === "delivered"
          ) {
            newReceivedItems[item.id] = false;
          } else {
            newReceivedItems[item.id] = false;
          }
          newOrderStatus[item.id] = item.status || "pending";
        });

        setReceivedItems(newReceivedItems);
        setOrderStatus(newOrderStatus);

        setPagination(
          response.data?.meta ||
          response.meta || {
            current_page: page,
            last_page: 1,
            per_page: 10,
            total: fetchedData.length || 0,
          },
        );

        console.log("Fetched data:", fetchedData);
      } else {
        console.error("API Error:", response?.message || "No response");
        setData([]);
        showErrorAlert(
          response?.message || t("driverRequest.alerts.fetchDataError"),
        );
      }
    } catch (error) {
      console.error(error);
      setData([]);
      showErrorAlert(t("driverRequest.alerts.fetchDataError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!["fast", "regular", "furnitures", "cars"].includes(currentType))
      return;
    handleFetch(1);
  }, [currentType]);

  const getAddress = (type, item, isPickup = true) => {
    if (type === "furnitures" || type === "cars") {
      if (isPickup) {
        return (
          item.transport?.booking_from ||
          t("driverRequest.general.notAvailable")
        );
      } else {
        return (
          item.transport?.booking_to || t("driverRequest.general.notAvailable")
        );
      }
    } else {
      if (isPickup) {
        return (
          item.booking?.booking_from?.[0] ||
          t("driverRequest.general.notAvailable")
        );
      } else {
        return (
          item.booking?.booking_to?.[0] ||
          t("driverRequest.general.notAvailable")
        );
      }
    }
  };

  const getBookingId = (type, item) => {
    if (type === "furnitures" || type === "cars") {
      return (
        item.transport?.booking_id || t("driverRequest.general.notAvailable")
      );
    } else {
      return (
        item.booking?.booking_id || t("driverRequest.general.notAvailable")
      );
    }
  };

  const getDateTime = (type, item, isPickup = true) => {
    if (type === "furnitures" || type === "cars") {
      const t_ = item.transport || {};
      if (isPickup) {
        const dateRaw = t_.loading_date_start || t_.loading_date || null;
        const dateEndRaw = t_.loading_date_end || null;
        return {
          date: dateRaw || t("driverRequest.general.notAvailable"),
          dateEnd: dateEndRaw || null,
          time:
            t_.loading_time_start ||
            t_.loading_time ||
            t("driverRequest.general.notAvailable"),
          timeEnd: t_.loading_time_end || null,
        };
      } else {
        const dateRaw = t_.unloading_date_start || t_.unloading_date || null;
        const dateEndRaw = t_.unloading_date_end || null;
        return {
          date: dateRaw || t("driverRequest.general.notAvailable"),
          dateEnd: dateEndRaw || null,
          time:
            t_.unloading_time_start ||
            t_.unloading_time ||
            t("driverRequest.general.notAvailable"),
          timeEnd: t_.unloading_time_end || null,
        };
      }
    } else {
      if (isPickup) {
        const loadingData = item.booking?.loading_date_time?.[0];
        if (loadingData) {
          const [date, ...timeParts] = loadingData.split(" ");
          return {
            date:
              formatDisplayDate(date) ||
              t("driverRequest.general.notAvailable"),
            time:
              timeParts.join(" ") || t("driverRequest.general.notAvailable"),
          };
        }
        return {
          date: t("driverRequest.general.notAvailable"),
          time: t("driverRequest.general.notAvailable"),
        };
      } else {
        const unloadingData = item.booking?.unloading_date_time?.[0];
        if (unloadingData) {
          const [date, ...timeParts] = unloadingData.split(" ");
          return {
            date:
              formatDisplayDate(date) ||
              t("driverRequest.general.notAvailable"),
            time:
              timeParts.join(" ") || t("driverRequest.general.notAvailable"),
          };
        }
        return {
          date: t("driverRequest.general.notAvailable"),
          time: t("driverRequest.general.notAvailable"),
        };
      }
    }
  };

  const getWeight = (type, item) => {
    if (type === "furnitures" || type === "cars") {
      return formatNumber(item.transport?.total_weight) + " kg";
    } else {
      return formatNumber(item.booking?.total_weight) + " kg";
    }
  };

  const getDistance = (type, item) => {
    let dist = "";
    if (type === "furnitures" || type === "cars") {
      dist = item.transport?.distance || t("driverRequest.general.notAvailable");
    } else {
      dist = item.booking?.distance || t("driverRequest.general.notAvailable");
    }
    return dist ? dist.replace("km", " km") : dist;
  };

  const parseCoordinates = (coordString) => {
    if (!coordString) return null;
    try {
      const [lat, lng] = coordString
        .split(",")
        .map((coord) => parseFloat(coord.trim()));
      return [lat, lng];
    } catch (error) {
      console.error("Error parsing coordinates:", error);
      return null;
    }
  };

  const getTypeIcon = (type, item) => {
    return (
      <TransportImage
        type={type}
        item={item}
        className="text-secondary mb-2"
        size={35}
      />
    );
  };

  const transportImages = {
    palette: paletteImg,
    pakete: paketeImg,
    document: documentImg,
    furniture: furnitureImg,
    electrical: electricalImg,
    tires: tiresImg,
    refrigerator: refrigeratorImg,
    "building materials": buildingMaterialsImg,
    moving: movingImg,
    bicycle: bicycleImg,
    motorcycle: motorcycleImg,
    car: carImg,
    "animal transport": animalTransportImg,
    other: otherImg,
    fast: fastImg,
    regular: regularImg,
    furnitures: furnitureImg,
    cars: carImg,
  };

  const TransportImage = ({ type, item, className = "", size = 100 }) => {
    const getImageSrc = () => {
      // 1. Try shipment_data (for regular/fast orders)
      const shipmentData = item.booking?.shipment_data || item.shipment_data;
      if (shipmentData && shipmentData.length > 0) {
        const firstShipment = shipmentData[0].shipment_data?.toLowerCase();
        if (firstShipment) {
          // Normalization: German terms -> English keys
          let normalizedKey = firstShipment === "pallete" ? "palette" : firstShipment;
          normalizedKey = normalizedKey === "dokument" ? "document" : normalizedKey;
          normalizedKey = normalizedKey === "baustoffe" ? "building materials" : normalizedKey;
          normalizedKey = normalizedKey === "elektrisch" ? "electrical" : normalizedKey;

          if (transportImages[normalizedKey]) {
            return transportImages[normalizedKey];
          }
        }
      }

      // 2. Try transport_type
      if (item?.transport_type) {
        const key = item.transport_type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      if (item.transport?.transport_type) {
        const key = item.transport.transport_type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      // 3. Try item type
      if (item?.type) {
        const key = item.type.toLowerCase();
        if (transportImages[key]) return transportImages[key];
      }

      return transportImages[type] || transportImages.other;
    };

    const imageSrc = getImageSrc();

    return (
      <img
        src={imageSrc}
        alt={type}
        className={className}
        style={{
          width: `${size}px`,
          height: `${size}px`,
          objectFit: "contain",
          // filter: "invert(1)"

        }}
        onError={(e) => {
          e.target.src = transportImages.other;
        }}
      />
    );
  };

  const getTypeText = (type, item) => {
    if (
      (type === "regular" || type === "fast") &&
      item.booking?.shipment_data?.length > 0
    ) {
      return item.booking.shipment_data.map((s) => t(s.shipment_data) || s.shipment_data).join(", ");
    }
    switch (type) {
      case "fast":
        return `${t(item.type) || t("driverCard.general.item")} · ${item.quantity || 1
          }`;
      case "regular":
        return `${t(item.type) || t("driverCard.general.item")} · ${item.quantity || 1
          }`;
      case "furnitures":
        return (
          t(item.transport?.transport_type) || t("driverCard.general.transport")
        );
      case "cars":
        return (
          t(item.transport?.item_type) || t("driverCard.general.transport")
        );
      default:
        return t("driverCard.general.item");
    }
  };



  const renderDriverInfo = (item) => {
    return (
      <div className="driver-info p-3 border rounded bg-light d-flex flex-column">
        <h6 className="fw-bold mb-3">
          <FaUser className="me-2 text-orange" />
          {t("driverRequest.driverInfo.title")}
        </h6>
        <Row>
          <Col md={12}>
            <p className="mb-2">
              <strong>{t("driverRequest.driverInfo.name")}:</strong>{" "}
              {item.vehicle_driver_name ||
                t("driverRequest.general.notAvailable")}
            </p>
          </Col>
          <Col md={12}>
            <p className="mb-2">
              <FaPhone className="me-2 text-muted" />
              <strong>{t("driverRequest.driverInfo.phone")}:</strong>{" "}
              {item.phone_number || t("driverRequest.general.notAvailable")}
            </p>
            <p className="mb-2">
              <FaStar className="me-2 text-warning" />
              <strong>{t("driverRequest.driverInfo.rating")}:</strong>{" "}
              {item.average_rating || "0"}
              <small className="text-muted">
                {" "}
                ({item.total_reviews || 0}{" "}
                {t("driverRequest.driverInfo.reviews")})
              </small>
            </p>
          </Col>
        </Row>
        <Row className="mt-2">
          <Col md={12}>
            <p className="mb-2">
              <strong>{t("driverRequest.driverInfo.vehicle")}:</strong>{" "}
              {item.vehicle_type || t("driverRequest.general.notAvailable")}
            </p>
          </Col>
          <Col md={12}>
            <p className="mb-2">
              <strong>{t("driverRequest.driverInfo.licensePlate")}:</strong>{" "}
              {item.vehicle_number || t("driverRequest.general.notAvailable")}
            </p>
          </Col>
        </Row>
        {item.current_location && (
          <Row className="mt-2">
            <Col>
              <p className="mb-0">
                <FaMapMarkerAlt className="me-2 text-danger" />
                <strong>
                  {t("driverRequest.driverInfo.currentLocation")}:
                </strong>{" "}
                {item.current_location}
              </p>
            </Col>
          </Row>
        )}
      </div>
    );
  };

  const getFromCoords = (type, item) => {
    const coords =
      type === "furnitures" || type === "cars"
        ? item.transport?.booking_from_lat_long
        : item.booking?.booking_from_lat_long;

    return parseCoordinates(coords);
  };

  const getToCoords = (type, item) => {
    const coords =
      type === "furnitures" || type === "cars"
        ? item.transport?.booking_to_lat_long
        : item.booking?.booking_to_lat_long;

    return parseCoordinates(coords);
  };

  // في دالة renderExpandedContent في DriverRequest.jsx
  const renderExpandedContent = (item, index) => {
    const itemReceived = isItemReceived(item.id);
    const unreadCount = unreadCounts[item.id] || 0;

    if (currentType === "regular" || currentType === "fast") {
      return (
        <BookingDetailsPage
          item={item.booking || {}}
          order={item}
          onClose={() =>
            setActiveKey((prev) => (prev === index.toString() ? null : prev))
          }
          onReceive={openReceiveConfirm}
          receivedLoading={receivedLoading}
          onDeliver={openDeliverConfirm}
          deliverLoading={deliverLoading}
          onChat={handleOpenChat}
          shouldShowChat={shouldShowChat(currentType)}
          currentType={currentType}
          itemReceived={itemReceived}
          showReceive={true}
          showDeliver={true}
          showDelete={false}
          showDriverDetails={true}
          showBookingDetails={true}
          showChat={false}
          showCMR={true}
          showID={true}
        />
      );
    } else {
      return (
        <TransportDetailsPage
          item={item.transport || {}}
          order={item}
          type={currentType}
          onClose={() =>
            setActiveKey((prev) => (prev === index.toString() ? null : prev))
          }
          onReceive={openReceiveConfirm}
          receivedLoading={receivedLoading}
          onDeliver={openDeliverConfirm}
          deliverLoading={deliverLoading}
          onChat={handleOpenChat}
          shouldShowChat={shouldShowChat(currentType)}
          itemReceived={itemReceived}
          showReceive={true}
          showDeliver={true}
          showDelete={false}
          showDriverDetails={true}
          showBookingDetails={true}
          showTransportDetails={true}
          showChat={true}
          showBoardNumber={true}
          unreadCount={unreadCount}
          showID={true}
        />
      );
    }
  };

  const toggleOffCanvas = (index) => {
    setOffCanvasOpen(offCanvasOpen === index ? null : index);
  };

  const renderActionButtons = (item) => {
    const itemReceived = isItemReceived(item.id);
    const itemCompleted = isItemCompleted(item.id);

    if (itemCompleted) {
      return (
        <div className="d-flex flex-column gap-2">
          <Button variant="success" size="sm" disabled>
            ✅ {t("driverRequest.buttons.completed")}
          </Button>
          {shouldShowChat(currentType) && (
            <Button
              variant="success"
              size="sm"
              onClick={() => handleOpenChat(item)}
            >
              💬 {t("driverRequest.buttons.chat")}
            </Button>
          )}
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-2">
        {!itemReceived ? (
          <Button
            className="bg-orange"
            size="sm"
            onClick={() => openReceiveConfirm(item, null)}
          >
            {t("driverRequest.buttons.receive")}
          </Button>
        ) : (
          <Button
            className="bg-orange"
            size="sm"
            onClick={() => openDeliverConfirm(item, null)}
          >
            {t("driverRequest.buttons.deliver")}
          </Button>
        )}

        {shouldShowChat(currentType) && (
          <Button
            variant="success"
            size="sm"
            onClick={() => handleOpenChat(item)}
          >
            💬 {t("driverRequest.buttons.chat")}
          </Button>
        )}
      </div>
    );
  };

  const shouldShowChat = (type) => {
    return type === "furnitures" || type === "cars";
  };

  const isItemReceived = (itemId) => {
    return receivedItems[itemId] || false;
  };

  const isItemCompleted = (itemId) => {
    return (
      orderStatus[itemId] === "completed" || orderStatus[itemId] === "delivered"
    );
  };

  const handleReceive = async () => {
    if (!selectedItem) return;

    setReceivedLoading(true);

    const data = {
      received_date: receiveDate,
      stage_number: selectedStageNumber,
    };

    try {
      let response;

      switch (currentType) {
        case "fast":
          response = await driverAPI.fastOrders.receive(selectedItem.id, data);
          break;
        case "regular":
          response = await driverAPI.bookingsOrders.receive(
            selectedItem.id,
            data,
          );
          break;
        case "cars":
        case "furnitures":
          response = await driverAPI.transportsOrders.receive(
            selectedItem.id,
            data,
          );
          break;
        default:
          console.error(t("driverRequest.errors.unknownOrderType"));
          showErrorAlert(t("driverRequest.errors.unknownOrderType"));
          return;
      }

      if (response.success) {
        setReceivedItems((prev) => ({
          ...prev,
          [selectedItem.id]: true,
        }));
        setOrderStatus((prev) => ({
          ...prev,
          [selectedItem.id]: "received",
        }));
        // setShowSuccess(true);
        setShowReceiveModal(false);
        setError(null);
        handleFetch(pagination.current_page);
        showSuccessAlert(t("driverRequest.alerts.receiveSuccess"));
      } else {
        const errorMsg =
          response.message || t("driverRequest.errors.receiveError");
        setError(errorMsg);
        showErrorAlert(errorMsg);
      }
    } catch (error) {
      console.error("Error receiving order:", error);
      const errorMsg =
        error.message || t("driverRequest.errors.receiveGeneralError");
      setError(errorMsg);
      showErrorAlert(errorMsg);
    } finally {
      setReceivedLoading(false);
    }
  };

  const handleComplete = async () => {
    if (!selectedItem) return;

    setDeliverLoading(true);

    const data = {
      completed_date: deliverDate,
      stage_number: selectedStageNumber,
    };

    try {
      let response;

      switch (currentType) {
        case "fast":
          response = await driverAPI.fastOrders.complete(selectedItem.id, data);
          break;
        case "regular":
          response = await driverAPI.bookingsOrders.complete(
            selectedItem.id,
            data,
          );
          break;
        case "cars":
        case "furnitures":
          response = await driverAPI.transportsOrders.complete(
            selectedItem.id,
            data,
          );
          break;
        default:
          console.error(t("driverRequest.errors.unknownOrderType"));
          showErrorAlert(t("driverRequest.errors.unknownOrderType"));
          return;
      }

      if (response.success) {
        setReceivedItems((prev) => ({
          ...prev,
          [selectedItem.id]: false,
        }));
        setOrderStatus((prev) => ({
          ...prev,
          [selectedItem.id]: "completed",
        }));
        // setShowSuccess(true);
        setShowDeliverModal(false);
        setError(null);
        handleFetch(pagination.current_page);
        showSuccessAlert(t("driverRequest.alerts.deliverySuccess"));
        // navigate("/driver/driver-completed");
      } else {
        const errorMsg =
          response.message || t("driverRequest.errors.deliveryError");
        setError(errorMsg);
        showErrorAlert(errorMsg);
      }
    } catch (error) {
      console.error("Error delivering order:", error);
      const errorMsg =
        error.message || t("driverRequest.errors.deliveryGeneralError");
      setError(errorMsg);
      showErrorAlert(errorMsg);
    } finally {
      setDeliverLoading(false);
    }
  };

  return (
    <>
      <NavbarMenu />
      <Container
        fluid
        className="p-4"
        style={{ minHeight: "100vh" }}
      >
        <Row className="text-center mb-4">
          <Col>
            <div className="professional-tabs">
              {userType === "driver" && (
                <Button
                  className={`tab-btn ${currentType === "fast" ? "bg-dark active" : "bg-orange"
                    }`}
                  onClick={() => setCurrentType("fast")}
                >
                  <FaRocket className="me-2" />
                  {t("driverRequest.orderTypes.fast")}
                </Button>
              )}
              <Button
                className={`tab-btn ${currentType === "regular" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => setCurrentType("regular")}
              >
                <FaBox className="me-2" />
                {t("driverRequest.orderTypes.regular")}
              </Button>
              <Button
                className={`tab-btn ${currentType === "furnitures" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => setCurrentType("furnitures")}
              >
                <FaCouch className="me-2" />
                {t("driverRequest.orderTypes.furniture")}
              </Button>
              <Button
                className={`tab-btn ${currentType === "cars" ? "bg-dark active" : "bg-orange"
                  }`}
                onClick={() => setCurrentType("cars")}
              >
                <FaCar className="me-2" />
                {t("driverRequest.orderTypes.car")}
              </Button>
            </div>
          </Col>
        </Row>

        <Accordion
          className="d-none d-md-block dark-accordion-container"
          activeKey={activeKey}
          onSelect={(key) => setActiveKey(key)}
        >
          {loading && (
            <p className="text-center p-3 dark-info-label">
              {t("driverRequest.general.loading")}
            </p>
          )}
          {!loading && (!Array.isArray(data) || data.length === 0) && (
            <p className="text-center p-3 dark-info-label">
              {t("driverRequest.general.noData")}
            </p>
          )}
          {!loading &&
            Array.isArray(data) &&
            data.map((item, index) => {
              const pickupDateTime = getDateTime(currentType, item, true);
              const deliveryDateTime = getDateTime(currentType, item, false);
              const pickupAddress = getAddress(currentType, item, true);
              const deliveryAddress = getAddress(currentType, item, false);
              const bookingId = getBookingId(currentType, item);
              const weight = getWeight(currentType, item);
              const distance = getDistance(currentType, item);

              return (
                <Accordion.Item
                  eventKey={index.toString()}
                  key={item.id || index}
                >
                  <Accordion.Header>
                    <div className="w-100 p-2">
                      {/* Header Top: Vehicle */}
                      {/* <div
                        className="d-flex align-items-center mb-3 pb-3"
                        style={{ borderBottom: "1px solid rgb(212 212 212)" }}
                      >
                        <TransportImage
                          type={currentType}
                          item={item}
                          size={32}
                        />
                        <span
                          style={{
                            // color: "#fff",
                            // fontWeight: "bold",
                            // fontSize: "1.1rem",
                            marginLeft: "0.8rem",
                          }}
                        >
                          <strong
                            className="pe-1 dark-info-label"
                            style={{ fontSize: "0.9rem" }}
                          >
                            Auftrag: {bookingId}
                          </strong>{" "}
                          | {getTypeText(currentType, item)}
                        </span>
                        <div className="ms-auto d-flex align-items-center gap-2">
                          {unreadCounts[item.id] > 0 && (
                            <Badge bg="danger">{unreadCounts[item.id]}</Badge>
                          )}
                          <Badge
                            bg="secondary"
                            className="text-white px-3 py-1"
                            style={{ borderRadius: "20px", fontSize: "0.8rem", backgroundColor: "#333" }}
                          >
                            {(currentType === "furnitures" || currentType === "cars")
                              ? (Array.isArray(item.transport?.booking_from) ? item.transport.booking_from.length : 1)
                              : (Array.isArray(item.booking?.booking_from) ? item.booking.booking_from.length : 1)}{" "}
                            {t("driverCard.general.stopsCount", "Stops")}
                          </Badge>
                        </div>
                      </div> */}

                      {/* Middle Content */}
                      <Row className="align-items-center g-2 px-2 py-3">

                         {/* Transport Image */}
                        <Col xs={12} md={2} className="text-center">
                          <TransportImage
                            type={currentType}
                            item={item}
                            // size={50}
                            width={180}
                          />

                          <div className="mb-1 d-flex align-items-center text-dark fw-bold justify-content-center">
                            <span>
                              <strong
                                className="pe-1 dark-info-label"
                                style={{ fontSize: "0.9rem" }}
                              >
                                Auftrag: {bookingId}
                              </strong>{" "}
                              | {getTypeText(currentType, item)}
                          </span>
                          </div>
                          
                        </Col>

                        {/* Pickup */}
                        <Col xs={12} md={2}>
                          <h6 className="text-orange fw-bold mb-2 small text-uppercase">
                            {t(
                              "driverCard.general.pickupTitle",
                              "ABHOLUNG (BELADUNG)",
                            )}
                          </h6>
                          {(Array.isArray(item.booking?.booking_from)
                            ? item.booking.booking_from
                            : [pickupAddress].filter(Boolean)
                          ).map((addr, i) => {
                            const rawDt =
                              item.booking?.loading_date_time?.[i] || "";
                            const spaceIdx = rawDt.indexOf(" ");
                            const dtDate =
                              spaceIdx > -1
                                ? rawDt.substring(0, spaceIdx)
                                : rawDt || pickupDateTime.date || "-";
                            const dtTime =
                              spaceIdx > -1
                                ? rawDt
                                  .substring(spaceIdx + 1)
                                  .replace(" - ", " → ")
                                : pickupDateTime.time
                                  ? pickupDateTime.time +
                                  (pickupDateTime.timeEnd
                                    ? ` → ${pickupDateTime.timeEnd}`
                                    : "")
                                  : "-";
                            return (
                              <div key={i} className="mb-2">
                                <div className="fw-bold text-orange mb-1 small">
                                  {t("driverCard.general.pickup")} ({i + 1})
                                </div>
                                <div className="mb-1 d-flex align-items-center text-dark fw-bold">
                                  <FaMapMarkerAlt className="text-orange me-2 fs-5 flex-shrink-0" />
                                  <span>{addr}</span>
                                </div>
                                <div
                                  className="p-2 d-inline-block"
                                  style={{
                                    backgroundColor: "inherit",
                                    borderRadius: "8px",
                                    border: "1px solid rgb(212 212 212 / 39%)",
                                  }}
                                >
                                  <div className="d-flex align-items-center mb-1 gap-3">
                                    <span
                                      className="d-flex align-items-center dark-info-label small"
                                      style={{ minWidth: "70px" }}
                                    >
                                      <FaCalendarAlt className="text-orange me-2" />{" "}
                                      {t("driverCard.general.dates", "Datum")}
                                    </span>
                                    <span className="text-dark fw-medium small flex-grow-1 text-start">
                                      {dtDate}
                                    </span>
                                  </div>
                                  <div
                                    className="d-flex align-items-center mt-1 pt-1 gap-3"
                                    style={{ borderTop: "1px solid rgb(212 212 212)" }}
                                  >
                                    <span
                                      className="d-flex align-items-center dark-info-label small"
                                      style={{ minWidth: "70px" }}
                                    >
                                      <AlarmIcon
                                        className="text-orange me-2"
                                        style={{ width: 16 }}
                                      />{" "}



                                      <span className="dark-info-label" style={{ marginLeft: "4px" }}>
                                        {t("driverCard.general.time", "Uhrzeit")}
                                      </span>
                                    </span>
                                    <span className="text-dark fw-medium small flex-grow-1 text-start d-flex align-items-center">
                                      {dtTime}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </Col>

                        {/* Arrow */}
                        <Col
                          xs={12}
                          md={1}
                          className="text-start d-none d-md-flex p-0"
                          style={{ position: "relative", minHeight: "100px" }}
                        >
                          <div
                            className="d-flex align-items-center justify-content-start"
                            style={{
                              position: "absolute",
                              top: "70%",
                              left: "0%",
                              transform: "translate(0%, -50%)",
                              minWidth: "80px",
                              width: "60%",
                              zIndex: 1,
                            }}
                          >
                            <div
                              style={{
                                height: "1px",
                                backgroundColor: "#6c757d",
                                flex: 1,
                              }}
                            ></div>
                            <div
                              style={{
                                width: "0",
                                height: "0",
                                borderTop: "4px solid transparent",
                                borderBottom: "4px solid transparent",
                                borderLeft: "6px solid #6c757d",
                              }}
                            ></div>
                          </div>
                        </Col>

                        {/* Delivery */}
                        <Col xs={12} md={2}>
                          <h6 className="text-orange fw-bold mb-2 small text-uppercase">
                            {t(
                              "driverCard.general.deliveryTitle",
                              "ZUSTELLUNG (ENTLADUNG)",
                            )}
                          </h6>
                          {(Array.isArray(item.booking?.booking_to)
                            ? item.booking.booking_to
                            : [deliveryAddress].filter(Boolean)
                          ).map((addr, i) => {
                            const rawDt =
                              item.booking?.unloading_date_time?.[i] || "";
                            const spaceIdx = rawDt.indexOf(" ");
                            const dtDate =
                              spaceIdx > -1
                                ? rawDt.substring(0, spaceIdx)
                                : rawDt || deliveryDateTime.date || "-";
                            const dtTime =
                              spaceIdx > -1
                                ? rawDt
                                  .substring(spaceIdx + 1)
                                  .replace(" - ", " → ")
                                : deliveryDateTime.time
                                  ? deliveryDateTime.time +
                                  (deliveryDateTime.timeEnd
                                    ? ` → ${deliveryDateTime.timeEnd}`
                                    : "")
                                  : "-";
                            return (
                              <div key={i} className="mb-2">
                                <div className="fw-bold text-orange mb-1 small">
                                  {t("driverCard.general.delivery")} ({i + 1})
                                </div>
                                <div className="mb-1 d-flex align-items-center text-dark fw-bold">
                                  <FaMapMarkerAlt className="text-secondary me-2 fs-5 flex-shrink-0" />
                                  <span>{addr}</span>
                                </div>
                                <div
                                  className="p-2 d-inline-block"
                                  style={{
                                    backgroundColor: "inherit",
                                    borderRadius: "8px",
                                    border: "1px solid rgb(212 212 212 / 39%)",
                                  }}
                                >
                                  <div className="d-flex align-items-center mb-1 gap-3">
                                    <span
                                      className="d-flex align-items-center dark-info-label small"
                                      style={{ minWidth: "70px" }}
                                    >
                                      <FaCalendarAlt className="text-orange me-2" />{" "}
                                      {t("driverCard.general.dates", "Datum")}
                                    </span>
                                    <span className="text-dark fw-medium small flex-grow-1 text-start">
                                      {dtDate}
                                    </span>
                                  </div>
                                  <div
                                    className="d-flex align-items-center mt-1 pt-1 gap-3"
                                    style={{ borderTop: "1px solid rgb(212 212 212)" }}
                                  >
                                    <span
                                      className="d-flex align-items-center dark-info-label small"
                                      style={{ minWidth: "70px" }}
                                    >
                                      <AlarmIcon
                                        className="text-orange me-2"
                                        style={{ width: 16 }}
                                      />{" "}
                                      <span className="dark-info-label" style={{ marginLeft: "4px" }}>
                                        {t("driverCard.general.time", "Uhrzeit")}
                                      </span>
                                    </span>
                                    <span className="text-dark fw-medium small flex-grow-1 text-start d-flex align-items-center">
                                      {dtTime}
                                    </span>
                                  </div>
                                </div>
                              </div>
                            );
                          })}
                        </Col>

                        {/* Stats */}
                        <Col xs={12} md={2} className="text-center">
                          <div
                            className="ps-md-2"
                            style={{ borderLeft: "1px solid rgb(212 212 212)" }}
                          >
                            <div
                              className="mb-2 pb-2"
                              style={{ borderBottom: "1px solid rgb(212 212 212)" }}
                            >
                              <div className="dark-info-label d-flex align-items-center justify-content-center mb-1">
                                <FaRoute className="me-2 text-secondary" />{" "}
                                {t("driverCard.general.route", "Route:")}
                              </div>
                              <div className="text-dark fw-bold fs-5">
                                {distance || "N/A"}
                              </div>
                            </div>
                            <div>
                              <div className="dark-info-label d-flex align-items-center justify-content-center mb-1">
                                <FaWeightHanging className="me-2 text-secondary" />{" "}
                                {t("driverCard.general.totalWeight", "Gewicht:")}
                              </div>
                              <div className="text-dark fw-bold fs-5">
                                {weight}
                              </div>
                            </div>
                          </div>
                        </Col>

                        {/* price */}
                        <Col xs={12} md={2} className="text-center">
                          <span className="card-price text-success">{item.price ? `${item.price} €` : ""}</span>

                          <div className="mb-1 d-flex align-items-center text-dark fw-bold justify-content-center">
                            <div
                              className={`${activeKey === index.toString() ? "open" : ""} py-2`}
                            >
                              <button className="btn-orange">{t("driverCard.general.showDetails")} →</button>
                            </div>
                          </div>
                          
                        </Col>
                      </Row>

                      {/* <div
                        className="d-flex justify-content-end mt-3 pt-2 pe-3"
                        // style={{ borderTop: "1px solid #333" }}
                      >
                        <div
                          className={`dark-artikel-collapse-icon ${activeKey === index.toString() ? "open" : ""}`}
                        >
                          <FaChevronDown
                            style={{
                              color: "#ff7b00",
                              transition: "transform 0.3s",
                              transform:
                                activeKey === index.toString()
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                            }}
                          />
                        </div>
                      </div> */}
                    </div>
                  </Accordion.Header>

                  <Accordion.Body>
                    <div
                      // className="dark-bg-darker"
                      style={{ borderRadius: "12px", padding: "1rem" }}
                    >
                      <Row>{renderExpandedContent(item, index)}</Row>
                    </div>
                  </Accordion.Body>
                </Accordion.Item>
              );
            })}
        </Accordion>

        <div className="d-block d-md-none">
          {loading && (
            <p className="text-center p-3">
              {t("driverRequest.general.loading")}
            </p>
          )}
          {!loading && (!Array.isArray(data) || data.length === 0) && (
            <p className="text-center p-3">
              {t("driverRequest.general.noData")}
            </p>
          )}
          {!loading &&
            Array.isArray(data) &&
            data.map((item, index) => {
              const pickupDateTime = getDateTime(currentType, item, true);
              const deliveryDateTime = getDateTime(currentType, item, false);
              const pickupAddress = getAddress(currentType, item, true);
              const deliveryAddress = getAddress(currentType, item, false);
              const bookingId = getBookingId(currentType, item);
              const weight = getWeight(currentType, item);
              const distance = getDistance(currentType, item);
              const stopsCount = (currentType === "furnitures" || currentType === "cars")
                ? (Array.isArray(item.transport?.booking_from) ? item.transport.booking_from.length : 1)
                : (Array.isArray(item.booking?.booking_from) ? item.booking.booking_from.length : 1);

              return (
                <div key={item.id || index} className="dark-mobile-card">
                  {/* Header: Artikel Title */}
                  <div className="dark-mobile-card-header">
                    <div className="dark-mobile-artikel-title">
                      <div>
                        <strong className="pe-1">Auftrag: {bookingId}</strong>
                      </div>

                      <TransportImage
                        type={currentType}
                        item={item}
                        className="dark-mobile-transport-img"
                        size={28}
                      />
                      <span>{getTypeText(currentType, item)}</span>
                    </div>
                    {unreadCounts[item.id] > 0 && (
                      <Badge bg="danger">{unreadCounts[item.id]}</Badge>
                    )}
                  </div>

                  {/* Body */}
                  <div className="dark-mobile-card-body">
                    {/* Location Info */}

                    {/* <div className="dark-mobile-location-row">
                      <div className="dark-mobile-location-item">
                        <div className="address">
                          <FaMapMarkerAlt />
                          <span>{pickupAddress}</span>
                        </div>
                        <div className="datetime">
                          <span className="date-line">
                            <FaCalendarAlt />
                            {pickupDateTime.date}
                          </span>
                          <span className="time-line">
                            <FaClock />
                            {pickupDateTime.time}
                          </span>
                        </div>
                      </div>

                      <div className="dark-mobile-location-item">
                        <div className="address">
                          <FaMapMarkerAlt className="to-icon" />
                          <span>{deliveryAddress}</span>
                        </div>
                        <div className="datetime">
                          <span className="date-line">
                            <FaCalendarAlt />
                            {deliveryDateTime.date}
                          </span>
                          <span className="time-line">
                            <FaClock />
                            {deliveryDateTime.time}
                          </span>
                        </div>
                      </div>
                    </div> */}

                    <div className="dark-mobile-location-row">
                      {Array.isArray(item.booking?.booking_from) &&
                        item.booking?.booking_from.length > 0 ? (
                        item.booking?.booking_from.map((location, i) => {
                          const rawDt =
                            item.booking?.loading_date_time?.[i] || "";
                          const spaceIdx = rawDt.indexOf(" ");
                          const dtDate =
                            spaceIdx > -1
                              ? rawDt.substring(0, spaceIdx)
                              : rawDt || "-";
                          const dtTime =
                            spaceIdx > -1
                              ? rawDt
                                .substring(spaceIdx + 1)
                                .replace(" - ", " → ")
                              : "-";

                          return (
                            <div
                              key={`pickup-${i}`}
                              className="dark-mobile-location-item"
                            >
                              <div className="address">
                                <FaMapMarkerAlt />
                                <span>{location}</span>
                              </div>

                              <div className="datetime">
                                <span className="date-line">
                                  <FaCalendarAlt />
                                  {dtDate}
                                </span>
                                <span className="date-line">
                                  <AlarmIcon style={{ width: 14 }} />
                                  {dtTime}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="dark-mobile-location-item">
                          <div className="address">
                            <FaMapMarkerAlt />
                            <span>
                              {pickupAddress ??
                                t("driverCard.general.notAvailable")}
                            </span>
                          </div>
                          <div className="datetime">
                            <span className="date-line">
                              <FaCalendarAlt />
                              {pickupDateTime.date}
                            </span>
                            <span className="time-line">
                              <FaClock />
                              {pickupDateTime.time}
                              {pickupDateTime.timeEnd
                                ? ` → ${pickupDateTime.timeEnd}`
                                : ""}
                            </span>
                          </div>
                        </div>
                      )}

                      {Array.isArray(item.booking?.booking_to) &&
                        item.booking?.booking_to.length > 0 ? (
                        item.booking?.booking_to.map((location, i) => {
                          const rawDt =
                            item.booking?.unloading_date_time?.[i] || "";
                          const spaceIdx = rawDt.indexOf(" ");
                          const dtDate =
                            spaceIdx > -1
                              ? rawDt.substring(0, spaceIdx)
                              : rawDt || "-";
                          const dtTime =
                            spaceIdx > -1
                              ? rawDt
                                .substring(spaceIdx + 1)
                                .replace(" - ", " → ")
                              : "-";

                          return (
                            <div
                              key={`delivery-${i}`}
                              className="dark-mobile-location-item"
                            >
                              <div className="address">
                                <FaMapMarkerAlt className="to-icon" />
                                <span>{location}</span>
                              </div>

                              <div className="datetime">
                                <span className="date-line">
                                  <FaCalendarAlt />
                                  {dtDate}
                                </span>
                                <span className="date-line">
                                  <AlarmIcon style={{ width: 14 }} />
                                  {dtTime}
                                </span>
                              </div>
                            </div>
                          );
                        })
                      ) : (
                        <div className="dark-mobile-location-item">
                          <div className="address">
                            <FaMapMarkerAlt />
                            <span>
                              {deliveryAddress ??
                                t("driverCard.general.notAvailable")}
                            </span>
                          </div>
                          <div className="datetime">
                            <span className="date-line">
                              <FaCalendarAlt />
                              {deliveryDateTime?.date || "-"}
                            </span>
                            <span className="time-line">
                              <FaClock />
                              {deliveryDateTime?.time || "-"}
                              {deliveryDateTime?.timeEnd
                                ? ` → ${deliveryDateTime.timeEnd}`
                                : ""}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Stats Row */}
                    <div className="dark-mobile-stats-row">
                      <div className="dark-mobile-stat-item">
                        <span className="stat-label">Route:</span>
                        <span className="stat-value">{distance}</span>
                      </div>
                      <div className="dark-mobile-stat-item">
                        <span className="stat-label">Stops:</span>
                        <span className="stat-value">{stopsCount}</span>
                      </div>
                      <div className="dark-mobile-stat-item">
                        <span className="stat-label">Gewicht:</span>
                        <span className="stat-value">{weight}</span>
                      </div>
                    </div>

                    {/* Footer: Button */}
                    <div className="dark-mobile-footer">
                      <div className="dark-mobile-price">
                        <span className="price-value">
                          {item.discounted_price || item.price || "0"} EUR
                        </span>
                        <span className="price-label">(Netto)</span>
                      </div>
                      <button
                        className="dark-mobile-details-btn"
                        onClick={() => toggleOffCanvas(index)}
                      >
                        {t("driverRequest.buttons.details")}
                        <FaChevronDown />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
        </div>

        {!loading &&
          Array.isArray(data) &&
          data.map((item, index) => {
            return (
              <div key={index}>
                <div
                  className={`offcanvas-overlay ${offCanvasOpen === index ? "show" : ""
                    }`}
                  onClick={() => setOffCanvasOpen(null)}
                />

                <div
                  className={`offcanvas-accordion-body ${offCanvasOpen === index ? "open" : ""
                    }`}
                >
                  <button
                    className="offcanvas-close-btn"
                    onClick={() => setOffCanvasOpen(null)}
                  >
                    <FaTimes />
                  </button>

                  <Card className="offcanvas-card">
                    <Card.Body>
                      <Row>
                        <Col xs={12}>
                          <div className="mb-3">
                            {renderExpandedContent(item, index)}
                          </div>
                        </Col>
                      </Row>
                    </Card.Body>
                  </Card>
                </div>
              </div>
            );
          })}

        {pagination.total > 0 && (
          <PaginationComponent
            currentPage={pagination.current_page}
            lastPage={pagination.last_page}
            onPageChange={handleFetch}
            disabled={loading}
          />
        )}

        <DriverChatModal
          show={showChatModal}
          handleClose={() => setShowChatModal(false)}
          transportId={selectedTransport?.transportId}
          orderId={selectedTransport?.orderId}
        />

        <Modal
          show={showReceiveModal}
          onHide={() => {
            setShowReceiveModal(false);
            setError(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("driverRequest.modals.confirmReceipt")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>{t("driverRequest.modals.receiveDate")}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={receiveDate}
                onChange={(e) => setReceiveDate(e.target.value)}
                min={new Date().toISOString().slice(0, 16)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowReceiveModal(false);
                setError(null);
              }}
            >
              {t("driverRequest.buttons.cancel")}
            </Button>
            <Button className="bg-orange" onClick={handleReceive}>
              {t("driverRequest.buttons.ok")}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showDeliverModal}
          onHide={() => {
            setShowDeliverModal(false);
            setError(null);
          }}
          centered
        >
          <Modal.Header closeButton>
            <Modal.Title>
              {t("driverRequest.modals.confirmDelivery")}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p>
              {t("driverRequest.modals.receiveDate")}: {receiveDate}
            </p>
            <Form.Group>
              <Form.Label>{t("driverRequest.modals.deliveryDate")}</Form.Label>
              <Form.Control
                type="datetime-local"
                value={deliverDate}
                onChange={(e) => setDeliverDate(e.target.value)}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onClick={() => {
                setShowDeliverModal(false);
                setError(null);
              }}
            >
              {t("driverRequest.buttons.cancel")}
            </Button>
            <Button className="bg-orange" onClick={handleComplete}>
              {t("driverRequest.buttons.ok")}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showSuccess}
          onHide={() => setShowSuccess(false)}
          centered
          backdrop="static"
        >
          <Modal.Body className="text-center p-4">
            <FaCheckCircle size={60} className="text-success mb-3" />
            <h5 className="fw-bold mb-3">
              {t("driverRequest.alerts.successfullyCompleted")}
            </h5>
            <Button
              variant="success"
              onClick={() => {
                setShowSuccess(false);
              }}
            >
              {t("driverRequest.buttons.ok")}
            </Button>
          </Modal.Body>
        </Modal>
      </Container>

      {(userType === "client" || userType === "driver" || userType === "driver_company") && (
        <ClientChatBox userType={userType} />
      )}
    </>
  );
};

export default DriverRequest;
